import { useState } from 'react';
import { StandardModal } from '@apply/components';
import { FileTypeName } from './components/FileTypeName';
import { UploadDirections } from './components/UploadDirections';
import { FileDropZone } from './components/FileDropZone/FileDropZone';
import { FileSelectFooter } from './components/FileSelectFooter';

interface FileSelectDialogProps {
    id: string;
    isOpen: boolean;
    /** The type the picked files will be added as, shown at the top. Omit to leave it out. */
    fileTypeName?: string;
    /** How many files can be picked at once (at least 1). Defaults to 1. */
    maxFiles?: number;
    /** Called with the picked files on Continue. Nothing is uploaded here; the caller does that. */
    onSubmit: (files: File[]) => void;
    onCancel: () => void;
}

/**
 * Picks files to upload: by "Select Files" or by dropping them on the panel, limited by type, size and count. A new
 * version of FileSelectDialog, on StandardModal. Each time it opens, it starts with nothing picked.
 */
export const FileSelectDialog = ({ id, isOpen, fileTypeName, maxFiles = 1, onSubmit, onCancel }: FileSelectDialogProps) => {
    const [files, set_files] = useState<File[]>([]);
    // A new key each time the dialog finishes closing, so the picker starts empty the next time it opens
    const [instanceKey, set_instanceKey] = useState(0);
    const fileLimit = Math.max(1, maxFiles);

    const clearPicked = () => {
        set_files([]);
        set_instanceKey((key) => key + 1);
    };

    return (
        <StandardModal
            id={id}
            isOpen={isOpen}
            onClose={onCancel}
            onHidden={clearPicked}
            width='550px'
            maxWidth='90vw'
            height='auto'
            hideOnOutsideClick={true}
            showModalHeader={true}
            modalTitle='File Upload'
            footer={
                <FileSelectFooter
                    id={id}
                    onCancel={onCancel}
                    onContinue={() => onSubmit(files)}
                    canContinue={files.length > 0}
                />
            }
        >
            <div
                key={instanceKey}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
                <FileTypeName fileTypeName={fileTypeName} />
                <UploadDirections maxFiles={fileLimit} />
                <FileDropZone
                    maxFiles={fileLimit}
                    onChange={set_files}
                />
            </div>
        </StandardModal>
    );
};
